<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useEligibilityStore } from '@/stores/useEligibilityStore'
import { isSiretOrSiren } from '@/utils/textUtils'

interface OdooCompany {
  id: number
  name: string
  siret?: string | null
  siren?: string | null
}

const props = defineProps<{
  modelValue: string | undefined
  label?: string
  placeholder?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: string | undefined]
  'select': [company: { uuid: string; name: string; metadata?: { street?: string; street2?: string; zip?: string; city?: string } }]
}>()

const eligibilityStore = useEligibilityStore()
const inputValue = ref('')
const showResults = ref(false)
const isLoading = ref(false)
const results = ref<OdooCompany[]>([])
const selectedCompany = ref<{ uuid: string; name: string } | null>(null)
let debounceTimeout: ReturnType<typeof setTimeout> | null = null

const canSearch = computed(() => {
  const v = inputValue.value.trim().replace(/\s/g, '')
  if (v.length === 0) return false
  if (isSiretOrSiren(v)) return true
  return v.length >= 3
})

async function doSearch() {
  const v = inputValue.value.trim()
  if (!canSearch.value) {
    results.value = []
    return
  }
  isLoading.value = true
  try {
    results.value = await eligibilityStore.searchOdooCompanies(v, 20)
    showResults.value = true
  } finally {
    isLoading.value = false
  }
}

function handleInput() {
  if (debounceTimeout) clearTimeout(debounceTimeout)
  debounceTimeout = setTimeout(doSearch, 300)
}

function handlePaste(event: ClipboardEvent) {
  const text = event.clipboardData?.getData('text') ?? ''
  if (text.trim().length >= 3) {
    setTimeout(doSearch, 100)
  }
}

async function selectCompany(company: OdooCompany) {
  isLoading.value = true
  try {
    const synced = await eligibilityStore.syncOdooPartner(company.id)
    if (synced?.uuid) {
      selectedCompany.value = { uuid: synced.uuid, name: synced.name ?? company.name }
      inputValue.value = ''
      showResults.value = false
      results.value = []
      emit('update:modelValue', synced.uuid)
      const meta = synced.metadata as { street?: string; street2?: string; zip?: string; city?: string } | undefined
      emit('select', {
        uuid: synced.uuid,
        name: synced.name ?? company.name,
        metadata: meta,
      })
    }
  } finally {
    isLoading.value = false
  }
}

function clearSelection() {
  selectedCompany.value = null
  emit('update:modelValue', undefined)
}

function handleBlur() {
  setTimeout(() => (showResults.value = false), 200)
}

watch(() => props.modelValue, (v) => {
  if (!v) selectedCompany.value = null
})
</script>

<template>
  <div class="relative">
    <div v-if="label" class="label">
      <span class="label-text font-semibold">{{ label }}</span>
    </div>
    <div class="input input-sm input-bordered w-full flex items-center gap-2 min-h-9">
      <span v-if="selectedCompany" class="flex-1 truncate">{{ selectedCompany.name }}</span>
      <input
        v-else
        type="text"
        v-model="inputValue"
        :placeholder="placeholder ?? 'Raison sociale ou SIREN/SIRET (3 car. min)'"
        class="flex-1 min-w-0 bg-transparent border-0 outline-none"
        @input="handleInput"
        @paste="handlePaste"
        @focus="canSearch && results.length > 0 && (showResults = true)"
        @blur="handleBlur"
      />
      <button
        v-if="selectedCompany"
        type="button"
        class="btn btn-ghost btn-xs btn-circle shrink-0"
        aria-label="Changer"
        @click="clearSelection"
      >
        ×
      </button>
    </div>
    <div v-if="isLoading" class="absolute z-10 w-full mt-1 bg-base-100 border rounded-md shadow-lg py-2 px-3 flex items-center justify-center">
      <span class="loading loading-spinner loading-xs me-2"></span> Recherche...
    </div>
    <div
      v-if="showResults && !isLoading && results.length > 0"
      class="absolute z-10 w-full mt-1 bg-base-100 border rounded-md shadow-lg max-h-80 overflow-y-auto"
    >
      <button
        v-for="c in results"
        :key="c.id"
        type="button"
        class="w-full text-left px-3 py-2 hover:bg-base-200 cursor-pointer border-b border-base-200 last:border-0"
        @mousedown.prevent="selectCompany(c)"
      >
        <div class="font-medium">{{ c.name }}</div>
        <div v-if="c.siret || c.siren" class="text-xs opacity-70">{{ c.siret || c.siren }}</div>
      </button>
    </div>
    <div
      v-if="showResults && !isLoading && inputValue.length > 0 && !canSearch"
      class="absolute z-10 w-full mt-1 bg-base-100 border rounded-md py-2 px-3"
    >
      <span class="text-sm opacity-70">3 caractères minimum pour la recherche par nom.</span>
    </div>
    <div
      v-if="showResults && !isLoading && canSearch && results.length === 0"
      class="absolute z-10 w-full mt-1 bg-base-100 border rounded-md py-2 px-3"
    >
      Aucun résultat pour "{{ inputValue }}"
    </div>
  </div>
</template>
